/**
 * ============================================================================
 * tiptapToMarkdown — Serializa um documento Tiptap (ProseMirror JSON) em
 *                    CommonMark + GFM, preservando cabeçalhos, listas,
 *                    tabelas, código, quotes, imagens, links, marcas inline
 *                    e blocos customizados da KB (callout, steps, toggle,
 *                    card, embed).
 *
 * Objetivo: produzir a "camada IA" a partir da "camada humana" (Tiptap JSON).
 *           Roda no cliente ao salvar e no backfill server-side (deno). Zero
 *           dependência externa — trabalha só com o JSON.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial. Suporta todos os blocos do
 *                                editor + custom nodes (callout, steps, toggle,
 *                                card, cardGrid, embed).
 * ============================================================================
 */

// ---------------------------------------------------------------------------
// Tipos mínimos do ProseMirror JSON (evita depender do runtime do Tiptap
// para funcionar também em Deno / Edge Functions).
// ---------------------------------------------------------------------------
export interface PMMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  text?: string;
  marks?: PMMark[];
}

// ---------------------------------------------------------------------------
// Escape helpers
// ---------------------------------------------------------------------------

/** Escapa caracteres reservados de markdown dentro de texto plano. */
function escapeMarkdownText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/([`*_{}\[\]()#+!|~])/g, "\\$1");
}

/** Escapa o conteúdo de uma célula de tabela (proíbe `|` e quebras). */
function escapeTableCell(text: string): string {
  return text.replace(/\|/g, "\\|").replace(/\n/g, " ");
}

// ---------------------------------------------------------------------------
// Aplicação de marcas inline em cima do texto
// ---------------------------------------------------------------------------

/** Aplica marcas Tiptap (bold, italic, code, link, strike, underline) em ordem estável. */
function applyMarks(text: string, marks: PMMark[] | undefined): string {
  if (!marks || marks.length === 0) return text;

  // Extrai link primeiro (fica por fora das outras marcações)
  const link = marks.find((m) => m.type === "link");
  const hasCode = marks.some((m) => m.type === "code");
  const hasBold = marks.some((m) => m.type === "bold");
  const hasItalic = marks.some((m) => m.type === "italic");
  const hasStrike = marks.some((m) => m.type === "strike");
  const hasUnderline = marks.some((m) => m.type === "underline");

  let out = text;

  // Código inline não recebe outras marcações (é literal)
  if (hasCode) {
    // Se contém crase, usa múltiplas crases como delimitador
    const matches: string[] = out.match(/`+/g) ?? [];
    const backticks: number = matches.reduce<number>(
      (max, b) => Math.max(max, b.length),
      0,
    );
    const fence = "`".repeat(backticks + 1);
    const pad = backticks > 0 ? " " : "";
    return `${fence}${pad}${out}${pad}${fence}`;
  }

  if (hasStrike) out = `~~${out}~~`;
  if (hasUnderline) out = `<u>${out}</u>`; // CommonMark não tem sublinhado; HTML inline é aceito por GFM
  if (hasBold && hasItalic) out = `***${out}***`;
  else if (hasBold) out = `**${out}**`;
  else if (hasItalic) out = `*${out}*`;

  if (link && link.attrs?.href) {
    const href = String(link.attrs.href).replace(/[)\s]/g, encodeURIComponent);
    const title = link.attrs?.title ? ` "${String(link.attrs.title).replace(/"/g, '\\"')}"` : "";
    out = `[${out}](${href}${title})`;
  }

  return out;
}

// ---------------------------------------------------------------------------
// Renderização de children inline (concatena text nodes aplicando marks)
// ---------------------------------------------------------------------------

function renderInline(nodes: PMNode[] | undefined): string {
  if (!nodes) return "";
  return nodes
    .map((n) => {
      if (n.type === "text") return applyMarks(escapeMarkdownText(n.text ?? ""), n.marks);
      if (n.type === "hardBreak") return "  \n"; // markdown hard break
      if (n.type === "image") {
        const alt = String(n.attrs?.alt ?? "");
        const src = String(n.attrs?.src ?? "");
        const title = n.attrs?.title ? ` "${String(n.attrs.title).replace(/"/g, '\\"')}"` : "";
        return `![${alt}](${src}${title})`;
      }
      // Fallback: se um bloco aparece no meio de inline (mal formado), renderiza recursivo
      return renderNode(n, 0);
    })
    .join("");
}

// ---------------------------------------------------------------------------
// Renderização de blocos
// ---------------------------------------------------------------------------

function renderList(
  node: PMNode,
  depth: number,
  ordered: boolean,
): string {
  const items = node.content ?? [];
  const start = ordered ? Number(node.attrs?.start ?? 1) : 0;

  return items
    .map((item, idx) => {
      const marker = ordered ? `${start + idx}. ` : "- ";
      const inner = (item.content ?? [])
        .map((child) => renderNode(child, depth + 1))
        .join("")
        .trim();

      // Indenta linhas subsequentes conforme largura do marcador
      const pad = " ".repeat(marker.length);
      const indented = inner
        .split("\n")
        .map((line, i) => (i === 0 ? line : line ? pad + line : line))
        .join("\n");

      return `${marker}${indented}`;
    })
    .join("\n");
}

function renderTable(node: PMNode): string {
  const rows = node.content ?? [];
  if (rows.length === 0) return "";

  const asRow = (r: PMNode): string[] =>
    (r.content ?? []).map((cell) =>
      escapeTableCell(
        (cell.content ?? []).map((c) => renderNode(c, 0)).join("").trim(),
      ),
    );

  const [headerRow, ...bodyRows] = rows;
  const headerCells = asRow(headerRow);
  const bodyCells = bodyRows.map(asRow);
  const colCount = Math.max(
    headerCells.length,
    ...bodyCells.map((r) => r.length),
  );

  const pad = (cells: string[]) => {
    const padded = [...cells];
    while (padded.length < colCount) padded.push("");
    return padded;
  };

  const lines: string[] = [];
  lines.push(`| ${pad(headerCells).join(" | ")} |`);
  lines.push(`| ${Array(colCount).fill("---").join(" | ")} |`);
  for (const r of bodyCells) lines.push(`| ${pad(r).join(" | ")} |`);
  return lines.join("\n");
}

/**
 * Renderiza um único nó do documento em markdown.
 * @param node Nó ProseMirror
 * @param depth Profundidade atual (usada para indentação em listas aninhadas)
 */
function renderNode(node: PMNode, depth: number): string {
  switch (node.type) {
    case "doc":
      return (node.content ?? [])
        .map((c) => renderNode(c, depth))
        .join("\n\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

    case "paragraph": {
      const inner = renderInline(node.content);
      return inner || "";
    }

    case "heading": {
      const level = Math.min(6, Math.max(1, Number(node.attrs?.level ?? 1)));
      return `${"#".repeat(level)} ${renderInline(node.content).trim()}`;
    }

    case "bulletList":
      return renderList(node, depth, false);

    case "orderedList":
      return renderList(node, depth, true);

    case "listItem":
      // Normalmente renderizado por renderList, mas caso apareça solto:
      return (node.content ?? [])
        .map((c) => renderNode(c, depth))
        .join("\n")
        .trim();

    case "taskList":
      return (node.content ?? [])
        .map((item) => {
          const checked = Boolean(item.attrs?.checked);
          const inner = (item.content ?? [])
            .map((c) => renderNode(c, depth + 1))
            .join("")
            .trim();
          return `- [${checked ? "x" : " "}] ${inner}`;
        })
        .join("\n");

    case "codeBlock": {
      const lang = String(node.attrs?.language ?? node.attrs?.language ?? "");
      const raw = (node.content ?? [])
        .map((c) => (c.type === "text" ? c.text ?? "" : ""))
        .join("");
      return `\`\`\`${lang}\n${raw}\n\`\`\``;
    }

    case "blockquote": {
      const inner = (node.content ?? [])
        .map((c) => renderNode(c, depth))
        .join("\n\n")
        .split("\n")
        .map((l) => `> ${l}`)
        .join("\n");
      return inner;
    }

    case "horizontalRule":
      return "---";

    case "hardBreak":
      return "  \n";

    case "image": {
      const alt = String(node.attrs?.alt ?? "");
      const src = String(node.attrs?.src ?? "");
      const title = node.attrs?.title ? ` "${String(node.attrs.title).replace(/"/g, '\\"')}"` : "";
      return `![${alt}](${src}${title})`;
    }

    case "table":
      return renderTable(node);

    // -----------------------------------------------------------------------
    // Custom blocks — projetados para round-trip via HTML comment marker
    // -----------------------------------------------------------------------

    case "callout": {
      const variant = String(node.attrs?.variant ?? "info");
      const inner = (node.content ?? [])
        .map((c) => renderNode(c, depth))
        .join("\n\n")
        .split("\n")
        .map((l) => `> ${l}`)
        .join("\n");
      // GitHub-style alerts (GFM) — reconhecidos por marked/remark
      return `> [!${variant.toUpperCase()}]\n${inner}`;
    }

    case "steps": {
      const items = node.content ?? [];
      return items
        .map((step, idx) => {
          const inner = (step.content ?? [])
            .map((c) => renderNode(c, depth + 1))
            .join("\n\n")
            .trim();
          const pad = "   ";
          const indented = inner
            .split("\n")
            .map((line, i) => (i === 0 ? line : line ? pad + line : line))
            .join("\n");
          return `${idx + 1}. ${indented}`;
        })
        .join("\n");
    }

    case "toggle": {
      const summary = String(node.attrs?.summary ?? "Detalhes");
      const inner = (node.content ?? [])
        .map((c) => renderNode(c, depth))
        .join("\n\n")
        .trim();
      // <details>/<summary> HTML — CommonMark aceita HTML inline
      return `<details>\n<summary>${summary}</summary>\n\n${inner}\n\n</details>`;
    }

    case "card": {
      const title = String(node.attrs?.title ?? "");
      const href = String(node.attrs?.href ?? "");
      const description = String(node.attrs?.description ?? "");
      return `- **${title}**${href ? ` — [${href}](${href})` : ""}${description ? `\n  ${description}` : ""}`;
    }

    case "cardGrid": {
      const items = (node.content ?? []).map((c) => renderNode(c, depth)).join("\n");
      return items;
    }

    case "embed": {
      const src = String(node.attrs?.src ?? "");
      const provider = String(node.attrs?.provider ?? "embed");
      return `[${provider}: ${src}](${src})`;
    }

    case "text":
      // caso um text apareça fora de contexto inline
      return applyMarks(escapeMarkdownText(node.text ?? ""), node.marks);

    default: {
      // Fallback: se for container desconhecido, renderiza filhos
      if (node.content && node.content.length > 0) {
        return node.content.map((c) => renderNode(c, depth)).join("\n\n");
      }
      return "";
    }
  }
}

/**
 * Ponto de entrada público. Aceita um documento ProseMirror completo (doc)
 * ou já um array de nós, e retorna markdown pronto para persistir em
 * kb_page_versions.content_markdown.
 */
export function tiptapToMarkdown(input: PMNode | { type: "doc"; content?: PMNode[] } | null | undefined): string {
  if (!input) return "";
  const doc: PMNode = input as PMNode;
  const out = renderNode(doc, 0);
  // Normalização final: colapsa >2 quebras seguidas em 2, remove espaços à direita
  return out
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
